import React from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import * as Yup from "yup"

import { gql, useClient, useMutation } from "hooks/urql"

import {
  AutocompleteField,
  Field,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"
import ModalFooter from "components/ModalWithProvidedBody/Footer"

export type NewThreadProps = {
  userType: "Student" | "Tutor" | "Admin" | "Parent"
}

const ValidationSchema = Yup.object().shape({
  userIds: Yup.array().required("You must select at least one recipient"),
  title: Yup.string().required("Please specify a title for this thread"),
})

const NewThread: React.FC<NewThreadProps> = ({ userType }) => {
  const client = useClient()
  const [, runMutation]: any[] = useMutation(createThreadMutation, client)
  const formState = {
    userIds: [],
    title: "",
  }
  const recipientEndpoint =
    userType === "Admin"
      ? "/api/admins/users/autocomplete_full_name"
      : `/api/${userType.toLowerCase()}s/message_threads/autocomplete_messageable_users`

  const handleSubmit = (values, actions) => {
    runMutation(values)
      .then(
        result => {
          const { failures, messageThread } = result.data.createMessageThread
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            NotificationManager.info("Thread created")
            window.location.href = messageThread.showPath
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  return (
    <ModalWithProvidedBody
      buttonClassName="btn solid blue btn-info"
      buttonText="New Message Thread"
      modalTitle="Create Message Thread"
      modalClassName="bootstrap-modal"
    >
      {({ closeModal }) => (
        <Formik
          initialValues={formState}
          validationSchema={ValidationSchema}
          onSubmit={handleSubmit}
        >
          <Form>
            <div className="modal-body">
              <AutocompleteField
                api={recipientEndpoint}
                name="userIds"
                label="Recipient Name(s)"
                isMulti
                valueAttribute={userType === "Admin" ? "id" : undefined}
                placeholder="Type Name..."
              />
              <Field
                name="title"
                label="Thread Title (this will be viewable by all parties)"
                placeholder="ex, 6th Grade Math"
              />
            </div>

            <ModalFooter
              cancelAction={closeModal}
              submitText="Create Thread"
              cancelText="Cancel"
            />
          </Form>
        </Formik>
      )}
    </ModalWithProvidedBody>
  )
}

const createThreadMutation = gql`
  mutation CreateMessageThread($userIds: [ID!]!, $title: String!) {
    createMessageThread(userIds: $userIds, title: $title) {
      failures {
        message
      }
      messageThread {
        id
        showPath
      }
    }
  }
`

export default NewThread
